import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import SEO, { breadcrumbJsonLd, organizationJsonLd } from '../../components/SEO';
import { SIGNUP_URL } from '../../data/links';

const PAGE_URL = 'https://geofleet.ai/about';
const HERO_IMAGE = 'https://cdn.geofleet.in/images/how-it-works-command-centre.png';
const DEMO_URL = 'https://meetings.hubspot.com/aditya145';
const CONTACT_EMAIL = 'care@geofleet.ai';

const PRINCIPLE_ICONS = ['ri-price-tag-3-line', 'ri-shield-user-line', 'ri-pulse-line', 'ri-lock-2-line'] as const;
const PRODUCT_ICONS = [
  'ri-dashboard-3-line',
  'ri-sparkling-2-line',
  'ri-route-line',
  'ri-smartphone-line',
  'ri-map-pin-user-line',
  'ri-bar-chart-box-line',
] as const;
const TRUST_ICONS = ['ri-shield-check-line', 'ri-award-line', 'ri-pulse-line', 'ri-lock-password-line'] as const;
const INDUSTRY_SLUGS = [
  'restaurants',
  'pharmacies',
  'couriers',
  'grocery',
  'retail',
  'ecommerce',
  'b2b-distribution',
  'field-service',
] as const;

type Stat = { value: string; label: string };
type Card = { title: string; body: string };
type Product = Card & { href: string };
type ConnectCard = Card & { cta: string };

function useList<T>(key: string, keys: (keyof T)[]): T[] {
  const { t, i18n } = useTranslation();
  return useMemo(() => {
    const raw = t(`aboutPage.${key}`, { returnObjects: true });
    if (!Array.isArray(raw)) return [];
    return raw.filter(
      (item): item is T =>
        typeof item === 'object' && item !== null && keys.every((k) => typeof (item as Record<string, unknown>)[k as string] === 'string')
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, i18n.language, key]);
}

export default function AboutPage() {
  const { t, i18n } = useTranslation();

  const stats = useList<Stat>('stats', ['value', 'label']);
  const principles = useList<Card>('principles', ['title', 'body']);
  const products = useList<Product>('products', ['title', 'body', 'href']);
  const trustItems = useList<Card>('trustItems', ['title', 'body']);
  const connectCards = useList<ConnectCard>('connectCards', ['title', 'body', 'cta']);
  const missionBody = useMemo(() => {
    const raw = t('aboutPage.missionBody', { returnObjects: true });
    return Array.isArray(raw) ? raw.filter((p): p is string => typeof p === 'string') : [];
  }, [t, i18n.language]);

  const connectLinks = [
    { href: SIGNUP_URL, external: true, icon: 'ri-rocket-2-line' },
    { href: DEMO_URL, external: true, icon: 'ri-calendar-check-line' },
    { href: '/partners', external: false, icon: 'ri-shake-hands-line' },
  ];

  return (
    <div className="pt-24">
      <SEO
        title={t('aboutPage.seoTitle')}
        description={t('aboutPage.seoDescription')}
        jsonLd={[
          breadcrumbJsonLd([
            { name: 'Home', url: 'https://geofleet.ai' },
            { name: 'About', url: PAGE_URL },
          ]),
          {
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: t('aboutPage.seoTitle'),
            url: PAGE_URL,
            description: t('aboutPage.seoDescription'),
            mainEntity: organizationJsonLd,
          },
        ]}
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-slate-50 via-white to-white pb-16 pt-10 lg:pb-24 lg:pt-14">
        <div className="pointer-events-none absolute right-0 top-0 h-[420px] w-[min(560px,90vw)] translate-x-1/4 -translate-y-1/3 rounded-full bg-blue-500/[0.07] blur-3xl" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-6 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-blue-100 bg-white px-4 py-2 text-sm font-semibold text-blue-700 shadow-sm">
              <i className="ri-building-4-line" aria-hidden />
              {t('aboutPage.badge')}
            </div>
            <h1 className="text-balance text-4xl font-bold tracking-tight text-gray-900 lg:text-5xl lg:leading-[1.08]">
              {t('aboutPage.title')} <span className="text-blue-600">{t('aboutPage.titleHighlight')}</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-gray-600">{t('aboutPage.lead')}</p>
            <dl className="mt-10 grid max-w-xl grid-cols-2 gap-6 sm:grid-cols-4">
              {stats.map((s) => (
                <div key={s.label} className="flex flex-col-reverse border-s-2 border-blue-600 ps-4">
                  <dt className="text-sm text-gray-500">{s.label}</dt>
                  <dd className="text-2xl font-bold tracking-tight text-gray-900">{s.value}</dd>
                </div>
              ))}
            </dl>
          </div>
          <div className="lg:col-span-6">
            <div className="relative">
              <div className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-blue-500/10 via-emerald-500/5 to-transparent blur-2xl" aria-hidden />
              <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white p-2 shadow-2xl shadow-gray-300/40">
                <img src={HERO_IMAGE} alt={t('aboutPage.heroImageAlt')} className="h-auto w-full rounded-xl" fetchPriority="high" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="bg-gray-950 py-20 lg:py-28" aria-labelledby="about-mission">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-emerald-400">{t('aboutPage.missionKicker')}</p>
            <h2 id="about-mission" className="mt-3 text-3xl font-bold tracking-tight text-white lg:text-4xl">
              {t('aboutPage.missionTitle')}
            </h2>
          </div>
          <div className="space-y-5 lg:col-span-7">
            {missionBody.map((para) => (
              <p key={para} className="text-lg leading-relaxed text-gray-300">
                {para}
              </p>
            ))}
            <blockquote className="mt-8 border-s-4 border-blue-500 ps-6 text-xl font-semibold leading-snug text-white">
              {t('aboutPage.missionQuote')}
            </blockquote>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="bg-white py-20 lg:py-28" aria-labelledby="about-principles">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-12 max-w-2xl">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-blue-700">{t('aboutPage.principlesKicker')}</p>
            <h2 id="about-principles" className="mt-2 text-3xl font-bold tracking-tight text-gray-900 lg:text-4xl">
              {t('aboutPage.principlesTitle')}
            </h2>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {principles.map((p, i) => (
              <article key={p.title} className="flex flex-col rounded-2xl border border-gray-200 bg-slate-50/60 p-6">
                <div className="mb-6 flex items-center justify-between">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-600 text-white">
                    <i className={`${PRINCIPLE_ICONS[i] ?? 'ri-checkbox-circle-line'} text-xl`} aria-hidden />
                  </span>
                  <span className="text-sm font-bold tabular-nums text-gray-300">{String(i + 1).padStart(2, '0')}</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-gray-600">{p.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Product */}
      <section className="border-t border-gray-100 bg-slate-50/70 py-20 lg:py-28" aria-labelledby="about-product">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mx-auto mb-12 max-w-2xl text-center">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-blue-700">{t('aboutPage.productKicker')}</p>
            <h2 id="about-product" className="mt-2 text-3xl font-bold tracking-tight text-gray-900 lg:text-4xl">
              {t('aboutPage.productTitle')}
            </h2>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {products.map((p, i) => (
              <Link
                key={p.title}
                to={p.href}
                className="group flex gap-4 rounded-2xl border border-gray-200 bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-blue-300 hover:shadow-md"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
                  <i className={`${PRODUCT_ICONS[i] ?? 'ri-apps-line'} text-xl`} aria-hidden />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center justify-between gap-2 font-semibold text-gray-900 group-hover:text-blue-700">
                    {p.title}
                    <i className="ri-arrow-right-line text-gray-300 group-hover:text-blue-600 rtl:rotate-180" aria-hidden />
                  </span>
                  <span className="mt-1 block text-sm leading-relaxed text-gray-600">{p.body}</span>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Industries + trust */}
      <section className="bg-white py-20 lg:py-28">
        <div className="mx-auto grid max-w-7xl gap-8 px-6 lg:grid-cols-2">
          <div className="rounded-3xl border border-gray-200 p-8 lg:p-10" aria-labelledby="about-industries">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-blue-700">{t('aboutPage.industriesKicker')}</p>
            <h2 id="about-industries" className="mt-2 text-2xl font-bold tracking-tight text-gray-900 lg:text-3xl">
              {t('aboutPage.industriesTitle')}
            </h2>
            <p className="mt-4 leading-relaxed text-gray-600">{t('aboutPage.industriesBody')}</p>
            <ul className="m-0 mt-6 flex list-none flex-wrap gap-2 p-0">
              {INDUSTRY_SLUGS.map((slug) => (
                <li key={slug}>
                  <Link
                    to={`/industries/${slug}`}
                    className="inline-flex rounded-full border border-gray-200 bg-slate-50 px-3.5 py-1.5 text-sm font-medium text-gray-700 hover:border-blue-300 hover:text-blue-700"
                  >
                    {t(`industries.${slug}.name`)}
                  </Link>
                </li>
              ))}
            </ul>
            <Link to="/industries" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 hover:text-blue-600">
              {t('aboutPage.industriesCta')}
              <i className="ri-arrow-right-line rtl:rotate-180" aria-hidden />
            </Link>
          </div>

          <div className="rounded-3xl bg-gray-950 p-8 text-white lg:p-10" aria-labelledby="about-trust">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-emerald-400">{t('aboutPage.trustKicker')}</p>
            <h2 id="about-trust" className="mt-2 text-2xl font-bold tracking-tight lg:text-3xl">
              {t('aboutPage.trustTitle')}
            </h2>
            <ul className="m-0 mt-8 grid list-none gap-6 p-0 sm:grid-cols-2">
              {trustItems.map((item, i) => (
                <li key={item.title} className="flex gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/10 text-emerald-300">
                    <i className={`${TRUST_ICONS[i] ?? 'ri-shield-check-line'} text-lg`} aria-hidden />
                  </span>
                  <span>
                    <span className="block font-semibold">{item.title}</span>
                    <span className="mt-1 block text-sm leading-relaxed text-gray-400">{item.body}</span>
                  </span>
                </li>
              ))}
            </ul>
            <a
              href={`mailto:${CONTACT_EMAIL}?subject=Security%20documentation`}
              className="mt-8 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-300 hover:text-emerald-200"
            >
              {t('aboutPage.trustCta')}
              <i className="ri-arrow-right-line rtl:rotate-180" aria-hidden />
            </a>
          </div>
        </div>
      </section>

      {/* Connect */}
      <section className="border-t border-gray-100 bg-slate-50/70 py-20 lg:py-24" aria-labelledby="about-connect">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mx-auto mb-12 max-w-2xl text-center">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-blue-700">{t('aboutPage.connectKicker')}</p>
            <h2 id="about-connect" className="mt-2 text-3xl font-bold tracking-tight text-gray-900 lg:text-4xl">
              {t('aboutPage.connectTitle')}
            </h2>
          </div>
          <div className="grid gap-5 md:grid-cols-3">
            {connectCards.map((card, i) => {
              const link = connectLinks[i];
              const className =
                'mt-6 inline-flex items-center gap-1.5 rounded-xl px-5 py-2.5 text-sm font-semibold ' +
                (i === 0 ? 'bg-blue-600 text-white hover:bg-blue-700' : 'border border-gray-300 bg-white text-gray-900 hover:border-gray-400');
              return (
                <article key={card.title} className="flex flex-col rounded-2xl border border-gray-200 bg-white p-7">
                  <span className="mb-5 flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
                    <i className={`${link?.icon ?? 'ri-arrow-right-line'} text-xl`} aria-hidden />
                  </span>
                  <h3 className="text-lg font-bold text-gray-900">{card.title}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-gray-600">{card.body}</p>
                  <div>
                    {link && link.external ? (
                      <a href={link.href} target="_blank" rel="noopener noreferrer" className={className}>
                        {card.cta}
                        <i className="ri-arrow-right-line rtl:rotate-180" aria-hidden />
                      </a>
                    ) : (
                      <Link to={link?.href ?? '/contact'} className={className}>
                        {card.cta}
                        <i className="ri-arrow-right-line rtl:rotate-180" aria-hidden />
                      </Link>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
          <p className="mt-10 text-center text-sm text-gray-500">
            {t('aboutPage.contactLine')}{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className="font-semibold text-blue-700 hover:text-blue-600">
              {CONTACT_EMAIL}
            </a>
          </p>
        </div>
      </section>
    </div>
  );
}
